import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Calendar, Check, CheckCircle2, UserCheck, ArrowLeft } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface Registration {
  id: string;
  attendance_date: string | null;
  user_id: string;
  course_id: string;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    position: string | null;
    school: string | null;
    school_district: string | null;
    photo_url: string | null;
  } | null;
}

interface Course {
  id: string;
  title: string;
  description: string | null;
  hours: number;
  event_type: string;
  start_date: string | null;
  max_participants: number | null;
}

const AttendanceVerification = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: course, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      return data as Course;
    },
    enabled: !!courseId,
  });

  const { data: registrations = [], isLoading: isLoadingRegistrations, refetch: refetchRegistrations } = useQuery({
    queryKey: ['course-registrations', courseId],
    queryFn: async () => {
      // First fetch the registrations that have a user_id (excluding auto-generated entries)
      const { data: registrationData, error: registrationError } = await supabase
        .from('course_registrations')
        .select('*')
        .eq('course_id', courseId)
        .eq('status', 'registered')
        .not('user_id', 'is', null); // Filter out registrations without a user_id

      if (registrationError) throw registrationError;

      // Then for each registration, fetch the profile separately
      const registrationsWithProfiles = await Promise.all(
        registrationData.map(async (registration) => {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', registration.user_id)
            .single();
          
          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return {
              ...registration,
              profiles: null
            };
          }
          
          return {
            ...registration,
            profiles: profileData
          };
        })
      );

      return registrationsWithProfiles as Registration[];
    },
    enabled: !!courseId,
  });

  const filteredRegistrations = registrations.filter(registration => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const fullName = `${registration.profiles?.first_name || ''} ${registration.profiles?.last_name || ''}`.toLowerCase();
    
    return (
      fullName.includes(query) ||
      (registration.profiles?.school && registration.profiles.school.toLowerCase().includes(query)) ||
      (registration.profiles?.position && registration.profiles.position.toLowerCase().includes(query))
    );
  });

  const handleAttendanceVerification = async (registrationId: string) => {
    if (!profile?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to verify attendance",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('course_registrations')
        .update({
          attendance_date: new Date().toISOString(),
          verified_by: profile.id,
          verified_at: new Date().toISOString()
        })
        .eq('id', registrationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Attendance verified successfully",
      });

      refetchRegistrations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to verify attendance",
        variant: "destructive",
      });
    }
  };

  const verifyAll = async () => {
    if (!profile?.id || !registrations.length) return;
    
    try {
      // Filter out already verified registrations
      const unverifiedRegistrations = registrations.filter(reg => !reg.attendance_date);
      
      if (!unverifiedRegistrations.length) {
        toast({
          title: "Information",
          description: "All attendees are already verified",
        });
        return;
      }
      
      const updates = unverifiedRegistrations.map(reg => ({
        id: reg.id,
        attendance_date: new Date().toISOString(),
        verified_by: profile.id,
        verified_at: new Date().toISOString()
      }));
      
      // Using Promise.all to handle multiple updates
      await Promise.all(updates.map(update => 
        supabase
          .from('course_registrations')
          .update({
            attendance_date: update.attendance_date,
            verified_by: update.verified_by,
            verified_at: update.verified_at
          })
          .eq('id', update.id)
      ));

      toast({
        title: "Success",
        description: `Verified attendance for ${unverifiedRegistrations.length} participants`,
      });

      refetchRegistrations();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to verify attendance for all participants",
        variant: "destructive",
      });
    }
  };

  if (isLoadingCourse) {
    return <div className="container py-24 px-6 font-body">Loading course details...</div>;
  }

  if (!course) {
    return <div className="container py-24 px-6 font-body">Course not found</div>;
  }

  return (
    <div className="w-full py-1 px-1 space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin/courses")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold font-title text-primary flex items-center gap-2">
              <UserCheck className="h-6 w-6" />
              Manage Attendance
            </h1>
          </div>
          <p className="text-sm text-muted-foreground font-subtitle">
            Verify participant attendance for course
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate(`/admin/courses/${courseId}`)}
          className="gap-2 font-section"
        >
          <BookOpen className="h-4 w-4" />
          View Course Details
        </Button>
      </div>

      <Card>
        <CardHeader className="bg-muted/50">
          <CardTitle className="text-xl font-title">{course.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-caption">Event Type</p>
              <p className="font-medium font-body">{course.event_type === 'live' ? 'Live Event' : 'Self-Paced'}</p>
            </div>
            {course.start_date && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-caption">Date</p>
                <p className="font-medium font-body flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {new Date(course.start_date).toLocaleDateString()}
                </p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-caption">CEU Hours</p>
              <p className="font-medium font-body">{course.hours}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-caption">Max Participants</p>
              <p className="font-medium font-body">{course.max_participants || 'Unlimited'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold font-heading">Registered Participants</h2>
          <Badge variant="secondary">
            {registrations.length}
          </Badge>
        </div>
        <Button 
          onClick={verifyAll}
          className="gap-2 font-section"
          disabled={registrations.length === 0}
        >
          <CheckCircle2 className="h-4 w-4" />
          Verify All Attendance
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <UserCheck className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search participants by name, position, or school..." 
            className="pl-10 font-body"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {isLoadingRegistrations ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : registrations.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent className="flex flex-col items-center justify-center text-center space-y-4">
            <UserCheck className="h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-medium font-heading">No registered participants</h3>
            <p className="text-sm text-muted-foreground max-w-sm font-body">
              There are currently no participants registered for this course.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-section">Participant</TableHead>
                <TableHead className="font-section">School / District</TableHead>
                <TableHead className="font-section">Position</TableHead>
                <TableHead className="font-section">Status</TableHead>
                <TableHead className="text-right font-section">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegistrations.map((registration) => (
                <TableRow key={registration.id}>
                  <TableCell className="font-medium font-body">
                    <div className="flex items-center gap-3">
                      {registration.profiles?.photo_url ? (
                        <img 
                          src={registration.profiles.photo_url} 
                          alt={`${registration.profiles.first_name} ${registration.profiles.last_name}`}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                          {registration.profiles?.first_name?.[0] || '?'}
                        </div>
                      )}
                      <div>
                        <div>{registration.profiles?.first_name} {registration.profiles?.last_name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-body">
                    <div className="text-sm">
                      {registration.profiles?.school || 'N/A'}
                      {registration.profiles?.school_district && (
                        <div className="text-xs text-muted-foreground font-caption">
                          {registration.profiles.school_district}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-body">
                    {registration.profiles?.position || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {registration.attendance_date ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100">
                        <Check className="mr-1 h-3 w-3" /> Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant={registration.attendance_date ? "outline" : "default"}
                      size="sm"
                      onClick={() => handleAttendanceVerification(registration.id)}
                      disabled={!!registration.attendance_date}
                      className="font-section"
                    >
                      {registration.attendance_date ? 'Verified' : 'Verify Attendance'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default AttendanceVerification;
